import { useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import {
  useWorkspaceStore,
  setWorkspaces,
  setCurrentWorkspace,
  setDeviceLocalCollections,
} from '../store/workspaceStore';
import { setCollections, setSchemas } from '../store/collectionsStore';
import {
  setEnvironments,
  setSelectedEnvironment,
  setWorkspaceVariables,
  setGlobalVariables,
} from '../store/variablesStore';
import { loadWorkspaceSnapshot } from '../services/workspaceStoreRouter';
import { collectionService } from '../services/collectionService';
import { workspaceService } from '../services/workspaceService';
import { loadCloudSchemas, listLocalWorkspaceMetasResolved } from '../services/localWorkspaceStore';
import { readMirror } from '../services/workspaceMirror';
import { enrichCloudCollectionsWithLinks } from '../services/localCollectionLink';
import { hydrateCollectionsFromApi } from '../utils/collectionFolders';
import { usesLocalDataPath, mergeRemoteAndLocalWorkspaces } from '../utils/workspaceStorage';
import { dedupeCollectionRequestIds } from '../utils/localIds';
import { normalizeSchemas } from '../utils/schemaTree';
import { loadAllDeviceLocalCollections } from '../utils/deviceLocalCollections';
import { isTauri } from '../utils/tauri';
import { GUEST_DEMO_COLLECTIONS } from '../utils/guestDemoData';

/**
 * Loads collections (+ schemas + variable scopes) for the current workspace:
 * guest demo / local snapshot / cloud fetch with access-check + device-link
 * enrichment + schema-from-mirror fallback. Extracted verbatim from App (same
 * effect bodies / dep arrays). Loading state stays App-owned (shared with the
 * workspaces-load effect + splash machine) and is passed in; the 4 stable
 * refs/setters are added to the dep array purely to silence the warnings that
 * param-passing introduces — the residual "missing canAccessWorkspace /
 * currentWorkspace / isLoadingCollections / toastT / user" warning is
 * pre-existing (trimmed deps), just relocated from App.
 *
 * @param {{
 *   initialLoadComplete: boolean,
 *   isLoadingCollections: boolean,
 *   hasLoadedCollectionsAfterAuth: boolean,
 *   setIsLoadingCollections: Function,
 *   setHasLoadedCollectionsAfterAuth: Function,
 *   collectionsLoadingRef: { current: any },
 *   snapshotHydratedRef: { current: any },
 *   canAccessWorkspace: Function,
 *   toastT: Function,
 * }} deps
 */
export function useCollectionsLoader({
  initialLoadComplete,
  isLoadingCollections,
  hasLoadedCollectionsAfterAuth,
  setIsLoadingCollections,
  setHasLoadedCollectionsAfterAuth,
  collectionsLoadingRef,
  snapshotHydratedRef,
  canAccessWorkspace,
  toastT,
}) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace);
  const workspaceReloadNonce = useWorkspaceStore((s) => s.workspaceReloadNonce);

  useEffect(() => {
    if (!currentWorkspace?.id) {
      snapshotHydratedRef.current = null;
      return;
    }
    snapshotHydratedRef.current = null;
  }, [currentWorkspace?.id, snapshotHydratedRef]);

  // Load collections for the current workspace
  useEffect(() => {
    const loadCollections = async () => {
      if (collectionsLoadingRef.current || isLoadingCollections) {
        return;
      }

      if (!currentWorkspace || !initialLoadComplete) {
        return;
      }

      const workspaceId = currentWorkspace.id;
      const local = usesLocalDataPath(currentWorkspace, isAuthenticated);

      if (!isAuthenticated) {
        collectionsLoadingRef.current = true;
        setIsLoadingCollections(true);
        try {
          const snap = await loadWorkspaceSnapshot(currentWorkspace);
          const cols =
            snap.collections?.length > 0 ? snap.collections : GUEST_DEMO_COLLECTIONS;
          setCollections(cols);
          setSchemas(normalizeSchemas(snap.schemas));
          setGlobalVariables(
            snap.globalVariables?.length > 0
              ? snap.globalVariables
              : [{ key: 'timestamp', value: Date.now().toString(), enabled: true }]
          );
          setWorkspaceVariables(snap.workspaceVariables || []);
          const envs = snap.environments || [];
          setEnvironments(envs);
          if (snap.selectedEnvironmentId) {
            const sel = envs.find((e) => String(e.id) === String(snap.selectedEnvironmentId));
            setSelectedEnvironment(sel || null);
          } else {
            setSelectedEnvironment(null);
          }
          setHasLoadedCollectionsAfterAuth(false);
          snapshotHydratedRef.current = workspaceId;
        } finally {
          collectionsLoadingRef.current = false;
          setIsLoadingCollections(false);
        }
        return;
      }

      if (local) {
        collectionsLoadingRef.current = true;
        setIsLoadingCollections(true);
        try {
          const snap = await loadWorkspaceSnapshot(currentWorkspace);
          setCollections(dedupeCollectionRequestIds(snap.collections || []));
          setSchemas(normalizeSchemas(snap.schemas));
          setGlobalVariables(snap.globalVariables || []);
          setWorkspaceVariables(snap.workspaceVariables || []);
          const envs = snap.environments || [];
          setEnvironments(envs);
          if (snap.selectedEnvironmentId) {
            const sel = envs.find((e) => String(e.id) === String(snap.selectedEnvironmentId));
            setSelectedEnvironment(sel || null);
          } else {
            setSelectedEnvironment(null);
          }
          if (!hasLoadedCollectionsAfterAuth) {
            setHasLoadedCollectionsAfterAuth(true);
          }
          snapshotHydratedRef.current = workspaceId;
        } finally {
          collectionsLoadingRef.current = false;
          setIsLoadingCollections(false);
        }
        return;
      }

      // Signed in but the profile has not landed yet: wait (this effect runs
      // again on user?.id). Checking access without a user read as "denied"
      // and logged a data-issue error + warning toast on a normal start
      // (seen in e2e/demo/teamCollabBoards.check.js B5, 24.10.2026).
      if (isAuthenticated && !user) return;

      if (isAuthenticated && !hasLoadedCollectionsAfterAuth) {
        setHasLoadedCollectionsAfterAuth(true);
      }

      collectionsLoadingRef.current = true;
      setIsLoadingCollections(true);

      try {

        if (!canAccessWorkspace(currentWorkspace, user)) {
          console.warn('User does not have access to workspace:', currentWorkspace.name || currentWorkspace.id);
          setCollections([]);

          if (currentWorkspace.type === 'team') {
            toastT('toast.teamWorkspaceNoAccess', 'info', { name: currentWorkspace.name });
          } else {
            console.error('Access denied to personal workspace - this might indicate a data issue');
            toastT('toast.accessIssueRefreshing', 'warning', { name: currentWorkspace.name });

            if (isAuthenticated) {
              try {
                const refreshedWorkspaces = await workspaceService.getWorkspaces();
                const allWorkspaces = Array.isArray(refreshedWorkspaces)
                  ? refreshedWorkspaces
                  : refreshedWorkspaces?.all_workspaces || [];
                const manifestLocals = await listLocalWorkspaceMetasResolved();
                const merged = mergeRemoteAndLocalWorkspaces(allWorkspaces, manifestLocals);
                setWorkspaces(merged);

                const updatedCurrentWorkspace = merged.find((ws) => ws.id === currentWorkspace.id);
                if (updatedCurrentWorkspace) {
                  setCurrentWorkspace(updatedCurrentWorkspace);
                  toastT('toast.workspaceRefreshed', 'success');
                } else {
                  setCurrentWorkspace(merged[0] || null);
                  toastT('toast.workspaceNotFoundDefault', 'info');
                }
              } catch (refreshError) {
                console.error('Failed to refresh workspace data:', refreshError);
                toastT('toast.refreshWorkspaceFailed', 'error');
              }
            }
          }
          return;
        }

        await new Promise((resolve) => setTimeout(resolve, 150));

        const workspaceCollections = await collectionService.getCollections(currentWorkspace.id);
        let deviceList = [];
        try {
          deviceList = await loadAllDeviceLocalCollections();
          setDeviceLocalCollections(deviceList);
        } catch (deviceErr) {
          console.warn('Could not load local snapshots for link badges:', deviceErr);
        }
        setCollections(
          enrichCloudCollectionsWithLinks(hydrateCollectionsFromApi(workspaceCollections), deviceList)
        );
        try {
          let loadedSchemas = normalizeSchemas(loadCloudSchemas(currentWorkspace.id));
          if (
            isTauri() &&
            (!loadedSchemas.items?.length && !loadedSchemas.folders?.length)
          ) {
            const mirrorSnap = await readMirror(currentWorkspace.id);
            loadedSchemas = normalizeSchemas(mirrorSnap.schemas);
          }
          setSchemas(loadedSchemas);
        } catch {
          setSchemas(normalizeSchemas(null));
        }
        // Mark cloud workspace as hydrated so the YAML mirror effect can write
        // its on-disk copy without clobbering during the load window.
        snapshotHydratedRef.current = workspaceId;
      } catch (error) {
        console.error('Failed to load collections:', error);
        console.error('Workspace details:', currentWorkspace);

        if (error.response?.status === 403 || error.message?.includes('403')) {
          console.warn('Access denied to workspace');
          setCollections([]);

          if (currentWorkspace.type === 'team') {
            toastT('toast.teamWorkspaceAccessDenied', 'warning', { name: currentWorkspace.name });
          } else {
            toastT('toast.accessDeniedWorkspace', 'error');
          }
        } else if (
          !error.message?.includes('401') &&
          !error.message?.includes('Unauthorized')
        ) {
          console.error('Other error loading collections:', error.message);
          toastT('toast.loadCollectionsFailed', 'error', { message: error.message });
        }
      } finally {
        collectionsLoadingRef.current = false;
        setIsLoadingCollections(false);
      }
    };

    loadCollections();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- a workspace/user id change is the only thing that should refetch; isLoadingCollections is set by this effect, so tracking it would loop, and the objects churn on every store write
  }, [
    isAuthenticated,
    currentWorkspace?.id,
    user?.id,
    initialLoadComplete,
    hasLoadedCollectionsAfterAuth,
    workspaceReloadNonce,
    collectionsLoadingRef,
    snapshotHydratedRef,
    setIsLoadingCollections,
    setHasLoadedCollectionsAfterAuth,
  ]);
}
